import React, { useEffect, useMemo, useState } from 'react';
import { Send, Plus, Trash2, Save, TestTube2, Rocket, X } from 'lucide-react';
import { adminFetch, adminFetchJson } from '../../lib/adminAuth';
import WeeklyNewArrivalsPreview from './WeeklyNewArrivalsPreview';
import CacheWarmerPanel from './CacheWarmerPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const emptyDraft = {
  name: '',
  subject: '',
  hero_title: '',
  hero_subtitle: '',
  body_paragraphs: [''],
  artwork_ids: [],
  cta_label: '',
  cta_url: '',
};

const CampaignsManager = () => {
  const [campaigns, setCampaigns] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState('');
  const [artworkQuery, setArtworkQuery] = useState('');

  const load = async () => {
    setLoading(true);
    const [c, a] = await Promise.all([
      adminFetchJson(`${API_URL}/api/admin/campaigns`).catch(() => []),
      fetch(`${API_URL}/api/artworks?available=true&limit=500`).then((r) => r.json()).catch(() => []),
    ]);
    setCampaigns(Array.isArray(c) ? c : []);
    setArtworks(Array.isArray(a) ? a : []);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const flash = (msg) => { setToast(msg); setTimeout(() => setToast(''), 3500); };

  const startNew = () => setDraft({ ...emptyDraft });

  const edit = (c) => setDraft({
    ...emptyDraft, ...c,
    body_paragraphs: c.body_paragraphs && c.body_paragraphs.length ? c.body_paragraphs : [''],
    artwork_ids: c.artwork_ids || [],
  });

  const save = async () => {
    setBusy('save');
    try {
      const method = draft.id ? 'PATCH' : 'POST';
      const url = draft.id
        ? `${API_URL}/api/admin/campaigns/${draft.id}`
        : `${API_URL}/api/admin/campaigns`;
      const body = { ...draft };
      delete body.id; delete body.status; delete body.created_at;
      delete body.updated_at; delete body.sent_at; delete body.last_test_at;
      delete body.sent_summary;
      body.body_paragraphs = (body.body_paragraphs || []).map((s) => s.trim()).filter(Boolean);
      const saved = await adminFetchJson(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      flash(draft.id ? 'Draft updated' : 'Draft saved');
      await load();
      setDraft({ ...emptyDraft, ...saved, body_paragraphs: saved.body_paragraphs?.length ? saved.body_paragraphs : [''] });
    } catch (e) { flash('Save failed: ' + (e.message || 'unknown')); }
    setBusy('');
  };

  const testSend = async () => {
    if (!draft?.id) { flash('Save the draft first, then test-send.'); return; }
    setBusy('test');
    try {
      const res = await adminFetchJson(`${API_URL}/api/admin/campaigns/${draft.id}/test-send`, { method: 'POST' });
      flash(`Test sent to ${res.test_recipient} (${res.sent}/${res.recipients})`);
      await load();
    } catch (e) { flash('Test send failed: ' + (e.message || 'unknown')); }
    setBusy('');
  };

  const fullSend = async () => {
    if (!draft?.id) { flash('Save the draft first.'); return; }
    if (!window.confirm('Send this campaign to ALL active subscribers? This cannot be undone.')) return;
    setBusy('send');
    try {
      const res = await adminFetchJson(`${API_URL}/api/admin/campaigns/${draft.id}/send?confirm=true&test_only=false`, { method: 'POST' });
      flash(`Blast sent — ${res.sent} delivered / ${res.failed} failed / ${res.recipients} recipients`);
      await load();
      setDraft(null);
    } catch (e) { flash('Send failed: ' + (e.message || 'unknown')); }
    setBusy('');
  };

  const remove = async (cid) => {
    if (!window.confirm('Delete this campaign draft?')) return;
    await adminFetch(`${API_URL}/api/admin/campaigns/${cid}`, { method: 'DELETE' });
    await load();
    if (draft?.id === cid) setDraft(null);
  };

  const filteredArtworks = useMemo(() => {
    const q = artworkQuery.trim().toLowerCase();
    // Empty query → empty list. Prevents 60 random works (typically the newest
    // batch) from crowding the picker before the admin has expressed intent.
    if (!q) return [];
    return artworks.filter((a) =>
      (a.title || '').toLowerCase().includes(q) ||
      (a.artistName || '').toLowerCase().includes(q) ||
      (a.slug || '').toLowerCase().includes(q)
    ).slice(0, 60);
  }, [artworkQuery, artworks]);

  const toggleArtwork = (slugOrId) => {
    if (!draft) return;
    const ids = new Set(draft.artwork_ids || []);
    if (ids.has(slugOrId)) ids.delete(slugOrId); else ids.add(slugOrId);
    setDraft({ ...draft, artwork_ids: [...ids] });
  };

  const artworkLookup = useMemo(() => {
    const m = new Map();
    artworks.forEach((a) => { m.set(a.id, a); if (a.slug) m.set(a.slug, a); });
    return m;
  }, [artworks]);

  return (
    <div className="p-8 max-w-[1400px] mx-auto" data-testid="admin-campaigns-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-light tracking-tight">Campaigns</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Compose, test-send to Gabrielle, then blast to all active subscribers.
          </p>
        </div>
        <button
          onClick={startNew}
          className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white text-xs uppercase tracking-widest hover:bg-neutral-800"
          data-testid="campaigns-new-btn"
        >
          <Plus className="w-4 h-4" /> New Campaign
        </button>
      </div>

      {toast && (
        <div className="mb-4 px-4 py-2 bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm rounded" data-testid="campaigns-toast">
          {toast}
        </div>
      )}

      {/* Approval gate for the automated Saturday new-arrivals newsletter */}
      <WeeklyNewArrivalsPreview />

      {/* One-off cache warmer for the shop tile images */}
      <CacheWarmerPanel />

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-6">
        {/* LIST */}
        <div className="border border-neutral-200 rounded bg-white">
          <div className="px-4 py-3 border-b bg-neutral-50 text-xs uppercase tracking-widest text-neutral-500">
            {loading ? 'Loading…' : `${campaigns.length} campaign${campaigns.length === 1 ? '' : 's'}`}
          </div>
          <ul className="divide-y divide-neutral-200 max-h-[70vh] overflow-y-auto">
            {campaigns.map((c) => (
              <li
                key={c.id}
                className={`p-4 hover:bg-neutral-50 cursor-pointer ${draft?.id === c.id ? 'bg-neutral-50' : ''}`}
                onClick={() => edit(c)}
                data-testid={`campaign-row-${c.id}`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate">{c.name || '(untitled)'}</div>
                    <div className="text-xs text-neutral-500 truncate">{c.subject || 'no subject'}</div>
                    <div className="text-[10px] uppercase tracking-widest mt-1">
                      <span className={c.status === 'sent' ? 'text-emerald-600' : 'text-amber-600'}>
                        {c.status}
                      </span>
                      {c.sent_summary && (
                        <span className="text-neutral-500 ml-2">
                          {c.sent_summary.sent}/{c.sent_summary.recipients} sent
                        </span>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); remove(c.id); }}
                    className="text-neutral-400 hover:text-red-600"
                    aria-label="Delete campaign"
                    data-testid={`campaign-delete-${c.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </li>
            ))}
            {!loading && campaigns.length === 0 && (
              <li className="p-6 text-sm text-neutral-500 text-center">
                No campaigns yet. Click <em>New Campaign</em> to compose your first blast.
              </li>
            )}
          </ul>
        </div>

        {/* COMPOSER */}
        <div className="border border-neutral-200 rounded bg-white p-6">
          {!draft ? (
            <div className="text-neutral-500 text-sm text-center py-24">
              Select a campaign on the left, or click <strong>New Campaign</strong> to start.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-medium tracking-tight">
                  {draft.id ? 'Edit campaign' : 'New campaign'}
                </h2>
                <button
                  onClick={() => setDraft(null)}
                  className="text-neutral-400 hover:text-black"
                  aria-label="Close composer"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <label className="block text-xs uppercase tracking-widest text-neutral-500">Internal name
                <input
                  className="mt-1 w-full border border-neutral-300 rounded px-3 py-2 text-sm text-neutral-900"
                  value={draft.name}
                  onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                  placeholder="e.g. April Vol III relaunch"
                  data-testid="campaign-name-input"
                />
              </label>

              <label className="block text-xs uppercase tracking-widest text-neutral-500">Subject line
                <input
                  className="mt-1 w-full border border-neutral-300 rounded px-3 py-2 text-sm text-neutral-900"
                  value={draft.subject}
                  onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
                  placeholder="New Volume III just launched…"
                  data-testid="campaign-subject-input"
                />
              </label>

              <label className="block text-xs uppercase tracking-widest text-neutral-500">Hero title
                <input
                  className="mt-1 w-full border border-neutral-300 rounded px-3 py-2 text-sm text-neutral-900"
                  value={draft.hero_title}
                  onChange={(e) => setDraft({ ...draft, hero_title: e.target.value })}
                  placeholder="Daily Bat, Volume III"
                  data-testid="campaign-hero-input"
                />
              </label>

              <label className="block text-xs uppercase tracking-widest text-neutral-500">Hero subtitle
                <input
                  className="mt-1 w-full border border-neutral-300 rounded px-3 py-2 text-sm text-neutral-900"
                  value={draft.hero_subtitle}
                  onChange={(e) => setDraft({ ...draft, hero_subtitle: e.target.value })}
                  placeholder="Sebastian Magnani · 7 new photographs from 2025"
                  data-testid="campaign-subtitle-input"
                />
              </label>

              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Body paragraphs</div>
                {(draft.body_paragraphs || []).map((p, i) => (
                  <div key={i} className="flex gap-2 mb-2">
                    <textarea
                      className="flex-1 border border-neutral-300 rounded px-3 py-2 text-sm text-neutral-900"
                      rows={2}
                      value={p}
                      onChange={(e) => {
                        const next = [...draft.body_paragraphs];
                        next[i] = e.target.value;
                        setDraft({ ...draft, body_paragraphs: next });
                      }}
                      placeholder="Dear friend of the gallery, …"
                      data-testid={`campaign-body-${i}`}
                    />
                    <button
                      onClick={() => {
                        const next = draft.body_paragraphs.filter((_, j) => j !== i);
                        setDraft({ ...draft, body_paragraphs: next.length ? next : [''] });
                      }}
                      className="text-neutral-400 hover:text-red-600 self-start mt-2"
                      aria-label="Remove paragraph"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <button
                  onClick={() => setDraft({ ...draft, body_paragraphs: [...(draft.body_paragraphs || []), ''] })}
                  className="text-xs text-neutral-500 hover:text-black"
                  data-testid="campaign-body-add"
                >
                  + Add paragraph
                </button>
              </div>

              <div>
                <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">Featured artworks
                  <span className="text-neutral-400 normal-case tracking-normal ml-2">
                    ({(draft.artwork_ids || []).length} selected · 3-across grid works best with 3/6/9)
                  </span>
                </div>
                <input
                  className="w-full border border-neutral-300 rounded px-3 py-2 text-sm text-neutral-900 mb-2"
                  placeholder="Search title, artist or slug…"
                  value={artworkQuery}
                  onChange={(e) => setArtworkQuery(e.target.value)}
                  data-testid="campaign-artwork-search"
                />
                {(draft.artwork_ids || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2" data-testid="campaign-artwork-picks">
                    {draft.artwork_ids.map((key) => {
                      const a = artworkLookup.get(key);
                      return (
                        <span
                          key={key}
                          className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-neutral-100 border border-neutral-300 rounded"
                        >
                          {a ? `${a.title} — ${a.artistName}` : key}
                          <button onClick={() => toggleArtwork(key)} aria-label="Remove">
                            <X className="w-3 h-3" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="border border-neutral-200 rounded max-h-56 overflow-y-auto">
                  {filteredArtworks.length === 0 ? (
                    <div className="px-3 py-6 text-center text-xs text-neutral-500">
                      {artworkQuery.trim()
                        ? 'No artworks match that search.'
                        : `Search above to browse ${artworks.length} available artworks — by title, artist, or slug.`}
                    </div>
                  ) : filteredArtworks.map((a) => {
                    const key = a.slug || a.id;
                    const on = draft.artwork_ids?.includes(key);
                    return (
                      <button
                        key={a.id}
                        onClick={() => toggleArtwork(key)}
                        className={`w-full text-left px-3 py-2 text-xs border-b hover:bg-neutral-50 flex items-center gap-2 ${on ? 'bg-neutral-100' : ''}`}
                        data-testid={`campaign-artwork-option-${key}`}
                      >
                        <span className={`w-3 h-3 border ${on ? 'bg-black border-black' : 'border-neutral-400'}`} />
                        <span className="flex-1">{a.title} <span className="text-neutral-500">— {a.artistName}</span></span>
                        <span className="text-neutral-500">{a.priceDisplay || ''}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <label className="block text-xs uppercase tracking-widest text-neutral-500">CTA label
                  <input
                    className="mt-1 w-full border border-neutral-300 rounded px-3 py-2 text-sm text-neutral-900"
                    value={draft.cta_label}
                    onChange={(e) => setDraft({ ...draft, cta_label: e.target.value })}
                    placeholder="See all seven pieces"
                    data-testid="campaign-cta-label"
                  />
                </label>
                <label className="block text-xs uppercase tracking-widest text-neutral-500">CTA URL
                  <input
                    className="mt-1 w-full border border-neutral-300 rounded px-3 py-2 text-sm text-neutral-900"
                    value={draft.cta_url}
                    onChange={(e) => setDraft({ ...draft, cta_url: e.target.value })}
                    placeholder="/artist/sebastian-magnani#magnani-vol-iii"
                    data-testid="campaign-cta-url"
                  />
                </label>
              </div>

              <div className="flex flex-wrap items-center gap-2 pt-4 border-t">
                <button
                  onClick={save}
                  disabled={busy === 'save'}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-black text-xs uppercase tracking-widest hover:bg-black hover:text-white disabled:opacity-40"
                  data-testid="campaign-save-btn"
                >
                  <Save className="w-4 h-4" /> {busy === 'save' ? 'Saving…' : (draft.id ? 'Update draft' : 'Save draft')}
                </button>
                <button
                  onClick={testSend}
                  disabled={!draft.id || busy === 'test'}
                  className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-400 text-xs uppercase tracking-widest hover:bg-neutral-100 disabled:opacity-40"
                  data-testid="campaign-test-btn"
                  title={draft.id ? '' : 'Save the draft first'}
                >
                  <TestTube2 className="w-4 h-4" /> {busy === 'test' ? 'Sending…' : 'Send test to Gabrielle'}
                </button>
                <button
                  onClick={fullSend}
                  disabled={!draft.id || busy === 'send' || draft.status === 'sent'}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-black text-white text-xs uppercase tracking-widest hover:bg-neutral-800 disabled:opacity-40"
                  data-testid="campaign-send-btn"
                  title={draft.status === 'sent' ? 'Already sent — duplicate to resend' : ''}
                >
                  <Rocket className="w-4 h-4" /> {busy === 'send' ? 'Sending…' : draft.status === 'sent' ? 'Already sent' : 'Send to all subscribers'}
                </button>
                {draft.last_test_at && (
                  <span className="text-xs text-neutral-500">
                    Last test: {new Date(draft.last_test_at).toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CampaignsManager;
